import cn from '../../utils/cn';

type Props = {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  className?: string;
};

function getPageRange(page: number, totalPages: number): (number | '...')[] {
  const always = new Set([1, 2, totalPages - 1, totalPages, page - 1, page, page + 1]);
  const pages = Array.from(always)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const result: (number | '...')[] = [];
  for (let i = 0; i < pages.length; i++) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) {
      result.push('...');
    }
    result.push(pages[i]);
  }

  return result;
}

export function Pagination({ page, totalPages, onChange, className }: Props) {
  if (totalPages <= 1) return null;

  const items = getPageRange(page, totalPages);

  return (
    <div className={cn('flex items-center justify-end gap-1 pt-3', className)}>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="px-2 py-1 text-lg text-neutral-500 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        ←
      </button>

      {items.map((item, i) =>
        item === '...' ? (
          <span key={`ellipsis-${i}`} className="px-1 py-1 text-lg text-neutral-400 select-none">
            ...
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            className={cn(
              'min-w-[28px] px-2 py-1 text-lg rounded transition-colors',
              item === page
                ? 'bg-neutral-800 text-white font-medium'
                : 'text-neutral-500 hover:bg-neutral-100',
            )}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="px-2 py-1 text-lg text-neutral-500 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        →
      </button>
    </div>
  );
}
